/**
 * API → view-model mapping for the Billing Summary listing.
 *
 * The backend already shapes each column, including the `{ up, down }` split a
 * `both`-type client produces, so this is mostly a rename plus two real
 * conversions: the Source string becomes a channel array for the icon cell, and
 * dates are formatted for display.
 */
/** True when the API split this column into a paid-media / affiliate pair. */
const isDual = (value) => typeof value === "object" && value !== null && !Array.isArray(value) && "up" in value && "down" in value;
/** Map one side's value through `fn`, preserving the up/down split. */
const mapDual = (value, fn) => isDual(value) ? { up: fn(value.up), down: fn(value.down) } : fn(value);
/** A column's contribution to the footer: both sides added when it's a pair. */
const dualSum = (value) => {
    const num = (v) => {
        const n = typeof v === "string" ? parseFloat(v) : v;
        return typeof n === "number" && !isNaN(n) ? n : 0;
    };
    return isDual(value) ? num(value.up) + num(value.down) : num(value);
};
/**
 * Measurement-channel display name → `NETWORK_METADATA_MAP` key, so the Source
 * column can render the real network icon. Keys are lowercased for robustness.
 *
 * The full set of values actually stored on `client_commission_goals` is: AWIN,
 * Impact, Google Ads, Shopify, AvantLink, Facebook Ads, Commission Junction,
 * Rakuten, Tab, None, Bing Ads, Levanta, Mgmt Fee Only, Pepperjam. "None" is
 * dropped server-side; "Tab" and "Mgmt Fee Only" have no network icon and fall
 * through to the initials circle, which is the intended treatment.
 */
const CHANNEL_NETWORK_KEYS = {
    "awin": "awin",
    "impact": "impact",
    "google ads": "adword",
    "shopify": "shopify",
    "avantlink": "avantlink",
    "facebook ads": "meta",
    "commission junction": "cj",
    "rakuten": "rakuten",
    "bing ads": "bing",
    "levanta": "levanta",
    "pepperjam": "pepperjam",
};
/**
 * "AWIN, Impact" → ["awin", "impact"]. Unmapped channels keep their display name
 * so the Source cell can label the fallback circle correctly.
 */
const toChannelKeys = (source) => {
    if (!source)
        return [];
    return source
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((name) => CHANNEL_NETWORK_KEYS[name.toLowerCase()] || name);
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-07-01" → "Jul 01, 2026". Anything unparseable passes through as-is. */
const formatDate = (iso) => {
    if (!iso)
        return "";
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match)
        return iso;
    const [, year, month, day] = match;
    const idx = Number(month) - 1;
    return idx >= 0 && idx < 12 ? `${MONTHS[idx]} ${day}, ${year}` : iso;
};
/** A dual date/notes column collapses to the first non-empty side for display. */
const firstText = (value) => {
    if (isDual(value))
        return String(value.up || value.down || "");
    return String(value || "");
};
const CLIENT_TYPE_LABELS = {
    paid_media: "Paid Media",
    affiliate: "Affiliate",
    both: "Both",
};
/**
 * Saved development-hour lines → repeater rows.
 *
 * `hours` and `rate` become strings because the modal's inputs are text-held until
 * save, and the `id` is a client-side row key only — it's scoped to the open dialog,
 * so indexing within the row is enough to keep it unique.
 */
const toDevelopmentHourEntries = (lines) => {
    if (!Array.isArray(lines))
        return [];
    return lines.map((line, index) => ({
        id: `saved_${index}`,
        type: line.type || "",
        description: line.description || "",
        // A stored 0 shows as an empty box rather than "0", matching a fresh line.
        hours: line.hours ? String(line.hours) : "",
        rate: line.rate ? String(line.rate) : "",
    }));
};
const numeric = (value) => mapDual(value, (side) => {
    const n = typeof side === "string" ? parseFloat(side) : side;
    return typeof n === "number" && !isNaN(n) ? n : 0;
});
/** Map the API rows into the listing's view model. */
export const mapBillingSummaryRows = (rows) => {
    if (!Array.isArray(rows))
        return [];
    return rows.map((row) => ({
        id: String(row.client_id),
        invoiceId: row.invoice_id || "",
        invoiceNo: row.invoice_number || "",
        client: row.client_name || "",
        // clientLinked: (!row.is_group_client && Number(row.comm_rate ?? 0) > 0) ? true : false,
        clientLinked: !row.is_group_client && (isDual(row.comm_rate)
            ? Number(row.comm_rate.up ?? 0) > 0 || Number(row.comm_rate.down ?? 0) > 0
            : Number(row.comm_rate ?? 0) > 0),
        isGroupClient: !!row.is_group_client,
        groupClients: (row.group_clients || []).map((c) => c.client_name).filter(Boolean),
        accountManagers: (row.account_managers || []).map((am) => ({
            name: am.name || "",
            initials: am.initials || "",
        })),
        internalStatus: row.internal_status || "Pending",
        invoiceStatus: row.invoice_status || "",
        invoiceStatusMsg: row.invoice_status_msg || "",
        invoiceDueAmount: Number(row.invoice_due_amount ?? 0),
        source: mapDual(row.source, (side) => toChannelKeys(side)),
        startDate: formatDate(firstText(row.start_date) || null),
        endDate: formatDate(firstText(row.end_date) || null),
        notes: firstText(row.notes),
        commDaysInMonth: mapDual(row.comm_day_month, (v) => (v === null ? null : Number(v))),
        commDaysBilled: mapDual(row.comm_day_billed_month, (v) => (v === null ? null : Number(v))),
        mgmtDaysInMonth: mapDual(row.mgmt_day_month, (v) => (v === null ? null : Number(v))),
        mgmtDaysBilled: mapDual(row.mgmt_day_billed_month, (v) => (v === null ? null : Number(v))),
        fullMgmtFee: numeric(row.full_mgmt_fees),
        billableMgmtFee: numeric(row.billable_mgmt_fees),
        commissionBaseline: numeric(row.threshold_value),
        revenueOrders: numeric(row.sales),
        commissionableAmount: numeric(row.commissionable_amount),
        commissionRate: numeric(row.comm_rate),
        commissionRateIsCurrency: false,
        commissions: Number(row.commissions ?? 0),
        // Left null when nothing is stored, so the modal can tell "never entered" from an
        // entered 0 and seed itself from the computed commission instead.
        commissionAmount: row.commission_amount === null || row.commission_amount === undefined
            ? null
            : Number(row.commission_amount),
        salesDescription: row.sales_description || "",
        developmentHours: Number(row.development_hours ?? 0),
        developmentHoursDetail: toDevelopmentHourEntries(row.development_hours_detail),
        totalBill: Number(row.total_bill ?? 0),
        invoiceAmount: Number(row.invoice_amount ?? 0),
        clientType: CLIENT_TYPE_LABELS[row.client_type] || "Paid Media",
        affiliatePer: numeric(row.affiliate_per),
        paidSocialPer: numeric(row.paid_social_per),
        paidSearchPer: numeric(row.paid_search_per),
        accountManagementPer: numeric(row.account_management_per),
        emailSmsPer: numeric(row.email_sms_per),
        totalPer: numeric(row.total_per),
        hasMultipleInvoice: !!row.has_multiple_invoice,
        // Only the ones that can actually be opened — an entry with no QuickBooks id would
        // render a PDF icon that leads nowhere.
        mappedInvoices: (row.mapped_invoices || [])
            .filter((line) => !!line?.invoice_id)
            .map((line) => ({
            invoiceId: String(line.invoice_id),
            invoiceNo: line.invoice_no || "",
            invoiceDate: formatDate(line.invoice_date),
            invoiceAmount: Number(line.invoice_amount ?? 0),
        })),
        quickbookConfigured: !!row.quickbook_configured,
    }));
};
/**
 * The extra QuickBooks invoices → the Mapping Invoice modal's rows.
 *
 * `mapped` becomes `selected`, which is what makes a re-opened modal reflect the database
 * rather than an empty selection.
 */
export const mapInvoiceMappingEntries = (invoices) => {
    if (!Array.isArray(invoices))
        return [];
    return invoices.map((invoice) => ({
        clientId: String(invoice.client_id),
        client: invoice.client_name || "",
        invoiceId: String(invoice.invoice_id),
        invoiceNo: invoice.invoice_no || "",
        invoiceDate: formatDate(invoice.invoice_date),
        invoiceAmount: Number(invoice.invoice_amount ?? 0),
        selected: !!invoice.mapped,
    }));
};
/**
 * Footer totals. The API returns no footer for this endpoint, so they're summed from
 * the rows it returned — which are the filtered ones, since all filtering is
 * server-side, so the total always matches what's on screen.
 */
export const mapBillingSummaryTotals = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    return list.reduce((totals, row) => ({
        fullMgmtFee: totals.fullMgmtFee + dualSum(row.full_mgmt_fees),
        billableMgmtFee: totals.billableMgmtFee + dualSum(row.billable_mgmt_fees),
        revenueOrders: totals.revenueOrders + dualSum(row.sales),
        commissionableAmount: totals.commissionableAmount + dualSum(row.commissionable_amount),
        commissions: totals.commissions + Number(row.commissions ?? 0),
        developmentHours: totals.developmentHours + Number(row.development_hours ?? 0),
        totalBill: totals.totalBill + Number(row.total_bill ?? 0),
        invoiceAmount: totals.invoiceAmount + Number(row.invoice_amount ?? 0),
    }), {
        fullMgmtFee: 0,
        billableMgmtFee: 0,
        revenueOrders: 0,
        commissionableAmount: 0,
        commissions: 0,
        developmentHours: 0,
        totalBill: 0,
        invoiceAmount: 0,
    });
};
